import React from 'react';
import { X, Download, Printer } from 'lucide-react';
import { Button } from './ui/button';

const MachineReportModal = ({ isOpen, onClose, reportData, onDownloadPDF }) => {
  if (!isOpen || !reportData) return null;

  const { machine, readings, prev_reading_before_window, service_logs, dispatches, financial_summary } = reportData;

  const handleDownloadPDF = () => {
    // Build clean HTML directly from data (don't copy innerHTML which loses Tailwind styles)
    const fmtNum = (n) => (n || 0).toLocaleString('el-GR');
    const fmt2 = (n) => (n || 0).toFixed(2);
    const fmt4 = (n) => (n || 0).toFixed(4);
    const safe = (v) => (v === undefined || v === null || v === '') ? '-' : v;

    // Calculate pages per reading (same logic as on-screen)
    const readingsRows = (readings || []).map((r, idx) => {
      let pb = 0, pc = 0, pa3 = 0, pa3c = 0;
      let prevB, prevC, prevA3, prevA3C;
      if (idx < (readings.length - 1)) {
        const prev = readings[idx + 1];
        prevB = prev.counter_value_black || 0;
        prevC = prev.counter_value_color || 0;
        prevA3 = prev.counter_value_a3 || 0;
        prevA3C = prev.counter_value_a3_color || 0;
      } else if (prev_reading_before_window) {
        prevB = prev_reading_before_window.counter_value_black || 0;
        prevC = prev_reading_before_window.counter_value_color || 0;
        prevA3 = prev_reading_before_window.counter_value_a3 || 0;
        prevA3C = prev_reading_before_window.counter_value_a3_color || 0;
      } else {
        prevB = machine.start_counter_black || 0;
        prevC = machine.start_counter_color || 0;
        prevA3 = machine.start_counter_a3 || 0;
        prevA3C = machine.start_counter_a3_color || 0;
      }
      pb = Math.max(0, (r.counter_value_black || 0) - prevB);
      pc = Math.max(0, (r.counter_value_color || 0) - prevC);
      pa3 = Math.max(0, (r.counter_value_a3 || 0) - prevA3);
      pa3c = Math.max(0, (r.counter_value_a3_color || 0) - prevA3C);
      return `<tr>
        <td class="c">${r.reading_year}</td>
        <td class="c">${r.reading_month}</td>
        <td class="r">${pb > 0 ? fmtNum(pb) : '-'}</td>
        <td class="r">${pc > 0 ? fmtNum(pc) : '-'}</td>
        <td class="r">${pa3 > 0 ? fmtNum(pa3) : '-'}</td>
        <td class="r">${pa3c > 0 ? fmtNum(pa3c) : '-'}</td>
      </tr>`;
    }).join('');

    const counterRows = (readings || []).map(r => `
      <tr>
        <td class="c">${r.reading_year}</td>
        <td class="c">${r.reading_month}</td>
        <td class="r">${fmtNum(r.counter_value_black)}</td>
        <td class="r">${fmtNum(r.counter_value_color)}</td>
        <td class="r">${fmtNum(r.counter_value_a3)}</td>
        <td class="r">${fmtNum(r.counter_value_a3_color)}</td>
        <td class="c small">${(r.date_recorded || r.reading_date || '').substring(0, 10)}</td>
      </tr>`).join('');

    const serviceRows = (service_logs && service_logs.length) ? service_logs.map(s => `
      <tr>
        <td class="c">${(s.service_date || '').substring(0, 10)}</td>
        <td>${safe(s.service_type)}</td>
        <td>${safe(s.description)}</td>
        <td>${safe(s.technician)}</td>
        <td>${safe(s.parts_text)}</td>
        <td class="r">${s.parts_cost > 0 ? fmt2(s.parts_cost) + '€' : '-'}</td>
        <td class="r">${s.labor_cost > 0 ? fmt2(s.labor_cost) + '€' : '-'}</td>
      </tr>`).join('') : `<tr><td colspan="7" class="c muted">Δεν υπάρχουν εγγραφές service</td></tr>`;

    const dispatchRows = (dispatches && dispatches.length) ? dispatches.map(d => `
      <tr>
        <td class="c">${(d.dispatch_date || '').substring(0, 10)}</td>
        <td>${safe(d.name)}</td>
        <td>${safe(d.category)}</td>
        <td class="c">${d.quantity}</td>
        <td class="r">${fmt2(d.price)}€</td>
        <td class="r">${fmt2((d.quantity || 0) * (d.price || 0))}€</td>
      </tr>`).join('') : `<tr><td colspan="6" class="c muted">Δεν υπάρχουν αποστολές</td></tr>`;

    const fs = financial_summary || {};
    const today = new Date().toLocaleDateString('el-GR');

    const html = `<!doctype html><html><head><meta charset="utf-8">
<title>Καρτέλα ${machine.machine_serial}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 10px; color: #1a1a1a; padding: 16px; }
  h1 { font-size: 16px; text-align: center; padding: 10px; background: #1f2937; color: #fff; margin-bottom: 4px; }
  .sub { text-align: center; font-size: 10px; color: #555; margin-bottom: 14px; }
  h2 { font-size: 12px; margin: 14px 0 6px; padding: 5px 8px; background: #e5e7eb; border-left: 4px solid #1f2937; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
  th, td { border: 1px solid #999; padding: 4px 6px; vertical-align: top; }
  th { background: #f3f4f6; font-weight: bold; font-size: 9px; text-align: left; }
  td { font-size: 9.5px; }
  .c { text-align: center; }
  .r { text-align: right; }
  .small { font-size: 8.5px; }
  .muted { color: #888; padding: 10px; }
  .grid2 { width: 100%; border-collapse: collapse; }
  .grid2 td { border: 1px solid #ccc; padding: 5px 8px; width: 50%; }
  .grid2 .lbl { font-size: 8.5px; color: #666; text-transform: uppercase; }
  .grid2 .val { font-size: 11px; font-weight: bold; }
  .summary { width: 100%; border-collapse: collapse; margin-top: 4px; }
  .summary td { border: 1px solid #999; padding: 5px 8px; font-size: 10px; }
  .summary .label { width: 65%; }
  .summary .amt { text-align: right; font-weight: bold; width: 35%; font-family: 'Courier New', monospace; }
  .total-row td { background: #f3f4f6; font-weight: bold; font-size: 11px; }
  .grand-total td { background: #1f2937; color: #fff; font-size: 13px; font-weight: bold; padding: 8px; }
  .footer { margin-top: 18px; text-align: center; font-size: 8px; color: #888; border-top: 1px solid #ccc; padding-top: 6px; }
  @page { size: A4; margin: 12mm; }
  @media print { body { padding: 0; } }
</style></head><body>

<h1>ΚΑΡΤΕΛΑ ΜΗΧΑΝΗΜΑΤΟΣ</h1>
<div class="sub">${machine.company_name || ''} &nbsp;|&nbsp; ${machine.brand || ''} ${machine.model || ''} &nbsp;|&nbsp; S/N: ${machine.machine_serial || ''} &nbsp;|&nbsp; Εκτύπωση: ${today}</div>

<h2>ΣΤΟΙΧΕΙΑ ΜΗΧΑΝΗΜΑΤΟΣ</h2>
<table class="grid2">
  <tr>
    <td><div class="lbl">Πελάτης</div><div class="val">${safe(machine.company_name)}</div></td>
    <td><div class="lbl">Μάρκα / Μοντέλο</div><div class="val">${safe(machine.brand)} ${safe(machine.model)}</div></td>
  </tr>
  <tr>
    <td><div class="lbl">Serial Number</div><div class="val">${safe(machine.machine_serial)}</div></td>
    <td><div class="lbl">Τύπος / Χρώμα</div><div class="val">${safe(machine.machine_type)} - ${safe(machine.machine_color)}</div></td>
  </tr>
  <tr>
    <td><div class="lbl">Εγκατάσταση</div><div class="val">${safe(machine.installation_date)}</div></td>
    <td><div class="lbl">Συμβόλαιο</div><div class="val">${safe(machine.contract_start)} έως ${safe(machine.contract_end)}</div></td>
  </tr>
  <tr>
    <td colspan="2">
      <div class="lbl">Αρχικοί Μετρητές</div>
      <div class="val">Black A4: ${fmtNum(machine.start_counter_black)} &nbsp;|&nbsp; Color A4: ${fmtNum(machine.start_counter_color)} &nbsp;|&nbsp; A3 B: ${fmtNum(machine.start_counter_a3)} &nbsp;|&nbsp; A3 C: ${fmtNum(machine.start_counter_a3_color)}</div>
    </td>
  </tr>
</table>

<h2>ΤΙΜΟΛΟΓΗΣΗ</h2>
<table>
  <tr>
    <th>Black A4</th><th>Color A4</th><th>A3 Black</th><th>A3 Color</th><th>Παγίο/Μήνα</th>
  </tr>
  <tr>
    <td class="c">${fmt4(machine.price_per_page_black)}€</td>
    <td class="c">${fmt4(machine.price_per_page_color)}€</td>
    <td class="c">${fmt4(machine.price_per_page_a3)}€</td>
    <td class="c">${fmt4(machine.price_per_page_a3_color)}€</td>
    <td class="c">${fmt2(machine.price_per_month)}€</td>
  </tr>
</table>

<h2>ΚΩΔΙΚΟΙ ΧΡΕΩΣΗΣ</h2>
<table>
  <tr>
    <th>Μηνιαίο Πάγιο</th><th>Ασπρόμ. Α4</th><th>Έγχρ. Α4</th><th>Ασπρόμ. Α3</th><th>Έγχρ. Α3</th>
  </tr>
  <tr>
    <td class="c">${(machine.charge_codes && machine.charge_codes.monthly) || '—'}</td>
    <td class="c">${(machine.charge_codes && machine.charge_codes.black) || '—'}</td>
    <td class="c">${(machine.charge_codes && machine.charge_codes.color) || '—'}</td>
    <td class="c">${(machine.charge_codes && machine.charge_codes.a3) || '—'}</td>
    <td class="c">${(machine.charge_codes && machine.charge_codes.a3_color) || '—'}</td>
  </tr>
</table>

<h2>ΠΡΟΣΦΑΤΕΣ ΜΕΤΡΗΣΕΙΣ - ΣΕΛΙΔΕΣ ΑΝΑ ΜΗΝΑ</h2>
<table>
  <tr>
    <th class="c">ΕΤΟΣ</th><th class="c">ΜΗΝΑΣ</th>
    <th class="r">BLACK A4</th><th class="r">COLOR A4</th>
    <th class="r">A3 BLACK</th><th class="r">A3 COLOR</th>
  </tr>
  ${readingsRows || '<tr><td colspan="6" class="c muted">Δεν υπάρχουν μετρήσεις</td></tr>'}
</table>

<h2>ΙΣΤΟΡΙΚΟ ΜΕΤΡΗΤΩΝ</h2>
<table>
  <tr>
    <th class="c">ΕΤΟΣ</th><th class="c">ΜΗΝΑΣ</th>
    <th class="r">BLACK A4</th><th class="r">COLOR A4</th>
    <th class="r">A3 BLACK</th><th class="r">A3 COLOR</th>
    <th class="c">ΗΜΕΡΟΜΗΝΙΑ</th>
  </tr>
  ${counterRows || '<tr><td colspan="7" class="c muted">Δεν υπάρχουν μετρητές</td></tr>'}
</table>

<h2>ΙΣΤΟΡΙΚΟ SERVICE</h2>
<table>
  <tr>
    <th class="c">ΗΜ/ΝΙΑ</th><th>ΤΥΠΟΣ</th><th>ΠΕΡΙΓΡΑΦΗ</th><th>ΤΕΧΝΙΚΟΣ</th>
    <th>ΑΝΤΑΛΛΑΚΤΙΚΑ</th><th class="r">ΚΟΣΤΟΣ ΑΝΤ.</th><th class="r">ΕΡΓΑΣΙΑ</th>
  </tr>
  ${serviceRows}
</table>

<h2>ΑΠΟΣΤΟΛΕΣ ΑΝΑΛΩΣΙΜΩΝ</h2>
<table>
  <tr>
    <th class="c">ΗΜ/ΝΙΑ</th><th>ΕΙΔΟΣ</th><th>ΚΑΤΗΓΟΡΙΑ</th>
    <th class="c">ΠΟΣΟΤ.</th><th class="r">ΤΙΜΗ ΜΟΝ.</th><th class="r">ΚΟΣΤΟΣ</th>
  </tr>
  ${dispatchRows}
</table>

<h2>ΣΥΝΟΛΙΚΑ ΟΙΚΟΝΟΜΙΚΑ ΣΤΟΙΧΕΙΑ</h2>
<table class="summary">
  <tr><td class="label">A4 Black: ${fmtNum(fs.total_pages_black)} σελ.</td><td class="amt">${fmt2(fs.cost_black)} €</td></tr>
  <tr><td class="label">A4 Color: ${fmtNum(fs.total_pages_color)} σελ.</td><td class="amt">${fmt2(fs.cost_color)} €</td></tr>
  <tr><td class="label">A3 Black: ${fmtNum(fs.total_pages_a3)} σελ.</td><td class="amt">${fmt2(fs.cost_a3)} €</td></tr>
  <tr><td class="label">A3 Color: ${fmtNum(fs.total_pages_a3_color)} σελ.</td><td class="amt">${fmt2(fs.cost_a3_color)} €</td></tr>
  <tr class="total-row"><td>ΣΥΝΟΛΟ ΣΕΛΙΔΩΝ</td><td class="amt">${fmt2(fs.total_pages_cost)} €</td></tr>
  <tr><td class="label">Παγίο (${fs.months_count || 0} μήνες)</td><td class="amt">+ ${fmt2(fs.total_monthly_fee)} €</td></tr>
  <tr><td class="label">Κόστος Σελίδων</td><td class="amt">+ ${fmt2(fs.total_pages_cost)} €</td></tr>
  <tr class="total-row"><td>ΣΥΝΟΛΟ ΕΣΟΔΩΝ</td><td class="amt">${fmt2(fs.total_revenue)} €</td></tr>
  <tr><td class="label">Αναλώσιμα</td><td class="amt">- ${fmt2(fs.total_consumables_cost)} €</td></tr>
  <tr><td class="label">Ανταλλακτικά Service</td><td class="amt">- ${fmt2(fs.total_parts_cost)} €</td></tr>
  <tr><td class="label">Κόστος Εργασίας</td><td class="amt">- ${fmt2(fs.total_labor_cost)} €</td></tr>
  ${fs.acquisition_cost > 0 ? `<tr><td class="label">ΑΡ.Χ (Κόστος αγοράς)</td><td class="amt">- ${fmt2(fs.acquisition_cost)} €</td></tr>` : ''}
  <tr class="total-row"><td>ΣΥΝΟΛΟ ΕΞΟΔΩΝ</td><td class="amt">${fmt2(fs.total_expenses)} €</td></tr>
  <tr class="grand-total"><td>ΚΕΡΔΟΣ (ΚΑΘΑΡΗ ΑΞΙΑ)</td><td class="amt">${fmt2(fs.grand_total)} €</td></tr>
  <tr class="total-row"><td>ΜΕ ΦΠΑ 24%</td><td class="amt">${fmt2(fs.grand_total_with_vat)} €</td></tr>
</table>

<div class="footer">MyPrinterManager.gr &nbsp;|&nbsp; Εκτύπωση καρτέλας: ${today}</div>

</body></html>`;

    // Hidden iframe (no popup blockers)
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error('Print error:', e);
      }
      setTimeout(() => {
        if (iframe && iframe.parentNode) document.body.removeChild(iframe);
      }, 1000);
    };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-[#004a99] text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">📄 ΚΑΡΤΕΛΑ ΜΗΧΑΝΗΜΑΤΟΣ</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" id="machine-report-content">
          {/* Machine Details */}
          <div className="mb-6 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
            <h3 className="text-xl font-bold mb-4 text-blue-900">📌 ΣΤΟΙΧΕΙΑ ΜΗΧΑΝΗΜΑΤΟΣ</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">ΠΕΛΑΤΗΣ</p>
                <p className="font-semibold text-lg">{machine.company_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">ΜΑΡΚΑ / ΜΟΝΤΕΛΟ</p>
                <p className="font-semibold">{machine.brand} {machine.model}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">SERIAL NUMBER</p>
                <p className="font-semibold">{machine.machine_serial}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">ΤΥΠΟΣ</p>
                <p className="font-semibold">{machine.machine_type} - {machine.machine_color}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">ΗΜΕΡΟΜΗΝΙΑ ΕΓΚΑΤΑΣΤΑΣΗΣ</p>
                <p className="font-semibold">{machine.installation_date || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">ΣΥΜΒΟΛΑΙΟ</p>
                <p className="font-semibold">{machine.contract_start || '-'} έως {machine.contract_end || '-'}</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-4 pt-3 border-t border-blue-200">
              <div>
                <p className="text-sm text-gray-600">ΑΡΧΙΚΟΣ Black A4</p>
                <p className="font-semibold">{(machine.start_counter_black || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">ΑΡΧΙΚΟΣ Color A4</p>
                <p className="font-semibold">{(machine.start_counter_color || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">ΑΡΧΙΚΟΣ A3 Black</p>
                <p className="font-semibold">{(machine.start_counter_a3 || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">ΑΡΧΙΚΟΣ A3 Color</p>
                <p className="font-semibold">{(machine.start_counter_a3_color || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="mb-6 bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
            <h3 className="text-lg font-bold mb-3 text-green-900">💰 ΤΙΜΟΛΟΓΗΣΗ</h3>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Black A4</p>
                <p className="font-semibold">{machine.price_per_page_black?.toFixed(4)}€</p>
              </div>
              <div>
                <p className="text-gray-600">Color A4</p>
                <p className="font-semibold">{machine.price_per_page_color?.toFixed(4)}€</p>
              </div>
              <div>
                <p className="text-gray-600">Παγίο/Μήνα</p>
                <p className="font-semibold">{machine.price_per_month?.toFixed(2)}€</p>
              </div>
              <div>
                <p className="text-gray-600">Τελ. Μετρητής Black</p>
                <p className="font-semibold">{machine.last_counter_black || 0}</p>
              </div>
            </div>
          </div>

          {/* Charge Codes */}
          <div className="mb-6 bg-amber-50 p-4 rounded-lg border-l-4 border-amber-500" data-testid="report-charge-codes">
            <h3 className="text-lg font-bold mb-3 text-amber-900">🧾 ΚΩΔΙΚΟΙ ΧΡΕΩΣΗΣ</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Μηνιαίο Πάγιο</p>
                <p className="font-semibold font-mono">{machine.charge_codes?.monthly || '—'}</p>
              </div>
              <div>
                <p className="text-gray-600">Ασπρόμ. Α4</p>
                <p className="font-semibold font-mono">{machine.charge_codes?.black || '—'}</p>
              </div>
              <div>
                <p className="text-gray-600">Έγχρ. Α4</p>
                <p className="font-semibold font-mono">{machine.charge_codes?.color || '—'}</p>
              </div>
              <div>
                <p className="text-gray-600">Ασπρόμ. Α3</p>
                <p className="font-semibold font-mono">{machine.charge_codes?.a3 || '—'}</p>
              </div>
              <div>
                <p className="text-gray-600">Έγχρ. Α3</p>
                <p className="font-semibold font-mono">{machine.charge_codes?.a3_color || '—'}</p>
              </div>
            </div>
          </div>

          {/* Recent Pages (calculated from counter differences) */}
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3">📊 ΠΡΌΣΦΑΤΕΣ ΜΕΤΡΗΣΕΙΣ (12 τελευταίοι μήνες)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse border">
                <thead>
                  <tr className="bg-blue-100">
                    <th className="border px-2 py-2">ΕΤΟΣ</th>
                    <th className="border px-2 py-2">ΜΗΝΑΣ</th>
                    <th className="border px-2 py-2">ΣΕΛΙΔΕΣ BLACK A4</th>
                    <th className="border px-2 py-2">ΣΕΛΙΔΕΣ COLOR A4</th>
                    <th className="border px-2 py-2">ΣΕΛΙΔΕΣ A3</th>
                  </tr>
                </thead>
                <tbody>
                  {readings && readings.length > 0 ? (
                    readings.map((r, idx) => {
                      let prevBlack, prevColor, prevA3;
                      if (idx < readings.length - 1) {
                        // Use next reading in array (previous chronologically)
                        const prev = readings[idx + 1];
                        prevBlack = prev.counter_value_black || 0;
                        prevColor = prev.counter_value_color || 0;
                        prevA3 = prev.counter_value_a3 || 0;
                      } else if (prev_reading_before_window) {
                        // Oldest in window: use the reading just before this window
                        prevBlack = prev_reading_before_window.counter_value_black || 0;
                        prevColor = prev_reading_before_window.counter_value_color || 0;
                        prevA3 = prev_reading_before_window.counter_value_a3 || 0;
                      } else {
                        // No previous reading at all: use machine start counters
                        prevBlack = machine.start_counter_black || 0;
                        prevColor = machine.start_counter_color || 0;
                        prevA3 = machine.start_counter_a3 || 0;
                      }
                      const pagesBlack = (r.counter_value_black || 0) - prevBlack;
                      const pagesColor = (r.counter_value_color || 0) - prevColor;
                      const pagesA3 = (r.counter_value_a3 || 0) - prevA3;
                      return (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border px-2 py-1 text-center">{r.reading_year}</td>
                          <td className="border px-2 py-1 text-center">{r.reading_month}</td>
                          <td className="border px-2 py-1 text-right font-semibold">{pagesBlack > 0 ? pagesBlack.toLocaleString() : '-'}</td>
                          <td className="border px-2 py-1 text-right font-semibold">{pagesColor > 0 ? pagesColor.toLocaleString() : '-'}</td>
                          <td className="border px-2 py-1 text-right font-semibold">{pagesA3 > 0 ? pagesA3.toLocaleString() : '-'}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="border px-2 py-4 text-center text-gray-500">
                        Δεν υπάρχουν μετρήσεις
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Counter History */}
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3">📈 ΙΣΤΟΡΙΚΟ ΜΕΤΡΗΤΩΝ (12 τελευταίοι μήνες)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse border">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-2 py-2">ΕΤΟΣ</th>
                    <th className="border px-2 py-2">ΜΗΝΑΣ</th>
                    <th className="border px-2 py-2">BLACK A4</th>
                    <th className="border px-2 py-2">COLOR A4</th>
                    <th className="border px-2 py-2">A3</th>
                    <th className="border px-2 py-2">ΗΜΕΡΟΜΗΝΙΑ</th>
                  </tr>
                </thead>
                <tbody>
                  {readings && readings.length > 0 ? (
                    readings.map((r, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border px-2 py-1 text-center">{r.reading_year}</td>
                        <td className="border px-2 py-1 text-center">{r.reading_month}</td>
                        <td className="border px-2 py-1 text-right">{r.counter_value_black?.toLocaleString()}</td>
                        <td className="border px-2 py-1 text-right">{r.counter_value_color?.toLocaleString() || '-'}</td>
                        <td className="border px-2 py-1 text-right">{r.counter_value_a3?.toLocaleString() || '-'}</td>
                        <td className="border px-2 py-1 text-center text-xs">{r.date_recorded?.substring(0, 10)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="border px-2 py-4 text-center text-gray-500">
                        Δεν υπάρχουν μετρήσεις
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Service Logs */}
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3">🔧 ΙΣΤΟΡΙΚΟ SERVICE (10 τελευταία)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse border">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-2 py-2">ΗΜΕΡΟΜΗΝΙΑ</th>
                    <th className="border px-2 py-2">ΤΥΠΟΣ</th>
                    <th className="border px-2 py-2">ΠΕΡΙΓΡΑΦΗ</th>
                    <th className="border px-2 py-2">ΤΕΧΝΙΚΟΣ</th>
                    <th className="border px-2 py-2">ΑΝΤΑΛΛΑΚΤΙΚΑ</th>
                    <th className="border px-2 py-2">ΚΟΣΤΟΣ ΑΝΤ.</th>
                    <th className="border px-2 py-2">ΚΟΣΤΟΣ ΕΡΓ.</th>
                  </tr>
                </thead>
                <tbody>
                  {service_logs && service_logs.length > 0 ? (
                    service_logs.map((s, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border px-2 py-1 text-center">{s.service_date?.substring(0, 10)}</td>
                        <td className="border px-2 py-1">{s.service_type || '-'}</td>
                        <td className="border px-2 py-1">{s.description || '-'}</td>
                        <td className="border px-2 py-1">{s.technician || '-'}</td>
                        <td className="border px-2 py-1 text-sm">
                          {s.parts_text || '-'}
                        </td>
                        <td className="border px-2 py-1 text-right font-semibold text-red-600">
                          {s.parts_cost > 0 ? `${s.parts_cost?.toFixed(2)}€` : '-'}
                        </td>
                        <td className="border px-2 py-1 text-right font-semibold text-orange-600">
                          {s.labor_cost > 0 ? `${s.labor_cost?.toFixed(2)}€` : '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="border px-2 py-4 text-center text-gray-500">
                        Δεν υπάρχουν εγγραφές service
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Consumables Dispatches */}
          <div className="mb-4">
            <h3 className="text-lg font-bold mb-3">📦 ΑΠΟΣΤΟΛΕΣ ΑΝΑΛΩΣΙΜΩΝ (10 τελευταίες)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse border">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-2 py-2">ΗΜΕΡΟΜΗΝΙΑ</th>
                    <th className="border px-2 py-2">ΕΙΔΟΣ</th>
                    <th className="border px-2 py-2">ΚΑΤΗΓΟΡΙΑ</th>
                    <th className="border px-2 py-2">ΠΟΣΟΤΗΤΑ</th>
                    <th className="border px-2 py-2">ΤΙΜΗ ΜΟΝ.</th>
                    <th className="border px-2 py-2">ΚΟΣΤΟΣ</th>
                  </tr>
                </thead>
                <tbody>
                  {dispatches && dispatches.length > 0 ? (
                    dispatches.map((d, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border px-2 py-1 text-center">{d.dispatch_date?.substring(0, 10)}</td>
                        <td className="border px-2 py-1">{d.name}</td>
                        <td className="border px-2 py-1">{d.category}</td>
                        <td className="border px-2 py-1 text-center">{d.quantity}</td>
                        <td className="border px-2 py-1 text-right">{d.price?.toFixed(2)}€</td>
                        <td className="border px-2 py-1 text-right font-semibold">{(d.quantity * d.price)?.toFixed(2)}€</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="border px-2 py-4 text-center text-gray-500">
                        Δεν υπάρχουν αποστολές
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Financial Summary */}
          {financial_summary && (
            <div className="mt-6 bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border-2 border-green-500">
              <h3 className="text-xl font-bold mb-4 text-green-900">💰 ΣΥΝΟΛΙΚΑ ΟΙΚΟΝΟΜΙΚΑ ΣΤΟΙΧΕΙΑ</h3>
              
              {/* Pages Summary */}
              <div className="mb-4 bg-white p-4 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">📄 Σελίδες ({financial_summary.months_count} μήνες)</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span>A4 Black: {financial_summary.total_pages_black?.toLocaleString()} σελ.</span>
                    <span className="font-semibold">{financial_summary.cost_black?.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between">
                    <span>A4 Color: {financial_summary.total_pages_color?.toLocaleString()} σελ.</span>
                    <span className="font-semibold">{financial_summary.cost_color?.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between">
                    <span>A3 Black: {financial_summary.total_pages_a3?.toLocaleString()} σελ.</span>
                    <span className="font-semibold">{financial_summary.cost_a3?.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between">
                    <span>A3 Color: {financial_summary.total_pages_a3_color?.toLocaleString()} σελ.</span>
                    <span className="font-semibold">{financial_summary.cost_a3_color?.toFixed(2)}€</span>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t flex justify-between font-semibold text-gray-800">
                  <span>ΣΥΝΟΛΟ ΣΕΛΙΔΩΝ:</span>
                  <span>{financial_summary.total_pages_cost?.toFixed(2)}€</span>
                </div>
              </div>

              {/* Revenue Section */}
              <div className="bg-white p-4 rounded-lg space-y-2 text-sm border-2 border-green-200">
                <h4 className="font-semibold text-green-700 mb-2">💰 ΕΣΟΔΑ</h4>
                <div className="flex justify-between">
                  <span>📄 Κόστος Σελίδων:</span>
                  <span className="font-semibold text-green-600">+{financial_summary.total_pages_cost?.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between">
                  <span>💳 Παγίο ({financial_summary.months_count} μήνες):</span>
                  <span className="font-semibold text-green-600">+{financial_summary.total_monthly_fee?.toFixed(2)}€</span>
                </div>
                <div className="mt-2 pt-2 border-t flex justify-between font-bold text-green-700">
                  <span>ΣΥΝΟΛΟ ΕΣΟΔΩΝ:</span>
                  <span>{financial_summary.total_revenue?.toFixed(2)}€</span>
                </div>
              </div>

              {/* Expenses Section */}
              <div className="bg-white p-4 rounded-lg space-y-2 text-sm border-2 border-red-200">
                <h4 className="font-semibold text-red-700 mb-2">📉 ΕΞΟΔΑ</h4>
                <div className="flex justify-between">
                  <span>📦 Αναλώσιμα:</span>
                  <span className="font-semibold text-red-600">-{financial_summary.total_consumables_cost?.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between">
                  <span>🔧 Ανταλλακτικά Service:</span>
                  <span className="font-semibold text-red-600">-{financial_summary.total_parts_cost?.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between">
                  <span>👨‍🔧 Κόστος Εργασίας:</span>
                  <span className="font-semibold text-red-600">-{financial_summary.total_labor_cost?.toFixed(2)}€</span>
                </div>
                {financial_summary.acquisition_cost > 0 && (
                  <div className="flex justify-between" data-testid="report-acquisition-cost">
                    <span>🛒 ΑΡ.Χ (Κόστος αγοράς):</span>
                    <span className="font-semibold text-red-600">-{financial_summary.acquisition_cost?.toFixed(2)}€</span>
                  </div>
                )}
                {financial_summary.arx_original > 0 && (
                  <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded" data-testid="arx-amortization-bar">
                    <div className="flex justify-between text-xs text-purple-700 mb-1">
                      <span>📊 Απόσβεση ΑΡ.Χ: {financial_summary.arx_consumed_total?.toFixed(2)}€ / {financial_summary.arx_original?.toFixed(2)}€</span>
                      <span className="font-bold">{financial_summary.arx_percent}%</span>
                    </div>
                    <div className="w-full bg-purple-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          (financial_summary.arx_percent || 0) >= 100 ? 'bg-emerald-500' :
                          (financial_summary.arx_percent || 0) >= 50  ? 'bg-blue-500' :
                          (financial_summary.arx_percent || 0) >= 20  ? 'bg-amber-500' : 'bg-red-400'
                        }`}
                        style={{ width: `${Math.min(financial_summary.arx_percent || 0, 100)}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-purple-600 mt-1">
                      Υπόλοιπο: {financial_summary.arx_remaining?.toFixed(2)}€
                      {financial_summary.arx_remaining <= 0 && ' • ✅ ΟΛΟΚΛΗΡΩΘΗΚΕ'}
                    </div>
                  </div>
                )}
                <div className="mt-2 pt-2 border-t flex justify-between font-bold text-red-700">
                  <span>ΣΥΝΟΛΟ ΕΞΟΔΩΝ:</span>
                  <span>{financial_summary.total_expenses?.toFixed(2)}€</span>
                </div>
              </div>

              {/* Profit Calculation */}
              <div className="bg-blue-50 p-4 rounded-lg space-y-2 text-sm border-2 border-blue-300">
                <div className="flex justify-between text-base">
                  <span>ΕΣΟΔΑ:</span>
                  <span className="font-semibold text-green-600">{financial_summary.total_revenue?.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between text-base">
                  <span>ΕΞΟΔΑ:</span>
                  <span className="font-semibold text-red-600">{financial_summary.total_expenses?.toFixed(2)}€</span>
                </div>
              </div>

              {/* Grand Total */}
              <div className="mt-4 bg-gradient-to-r from-green-600 to-blue-600 text-white p-4 rounded-lg shadow-lg">
                <div className="flex justify-between items-center text-lg">
                  <span className="font-bold">ΚΕΡΔΟΣ (ΚΑΘΑΡΗ ΑΞΙΑ):</span>
                  <span className="text-2xl font-bold">{financial_summary.grand_total?.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between items-center mt-2 text-base">
                  <span>ΜΕ ΦΠΑ 24%:</span>
                  <span className="text-xl font-bold">{financial_summary.grand_total_with_vat?.toFixed(2)}€</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 px-6 py-4 flex gap-3 justify-end border-t">
          <Button onClick={onClose} variant="outline">
            ΚΛΕΙΣΙΜΟ
          </Button>
          <Button 
            onClick={() => window.print()} 
            className="bg-gray-600 hover:bg-gray-700 flex items-center gap-2"
          >
            <Printer className="h-4 w-4" />
            ΕΚΤΥΠΩΣΗ
          </Button>
          <Button 
            onClick={handleDownloadPDF}
            className="bg-[#004a99] hover:bg-[#003a7a] flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            ΑΠΟΘΗΚΕΥΣΗ PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MachineReportModal;
